import { createAuthClient } from "better-auth/react";
import { apiBaseUrl } from "./runtime-config";
import { authToken, clearAuthToken, setAuthToken } from "./auth-token";
import { queryClient } from "./query-client";
import { clearScope } from "./scope";

// Where /api/auth/* lives. Resolved at page load from runtime-config so
// the container's entrypoint can supply the URL from Railway env vars
// (macro-resolved) without baking it into the bundle.
const baseURL = apiBaseUrl();

// Auth is BEARER-FIRST: the server (better-auth + bearer() plugin)
// returns the session token in the `set-auth-token` response header on
// sign-up/sign-in/refresh; we persist it in localStorage and send it
// back as `Authorization: Bearer <token>` on every subsequent request.
//
// We also set `credentials: "include"` so the same calls send any
// session cookie Better-Auth may have set. Cookies are not the
// primary carrier — they're the bridge that lets a social-signin
// redirect (which lands on the SPA without ever touching localStorage)
// be exchanged for a bearer token on the next /api/auth/get-session
// call. After that one call the bearer dominates.
export const authClient = createAuthClient({
  baseURL,
  fetchOptions: {
    credentials: "include",
    auth: {
      type: "Bearer",
      token: () => authToken() ?? "",
    },
    onSuccess: (ctx) => {
      const fresh = ctx.response.headers.get("set-auth-token");
      if (fresh) setAuthToken(fresh);
    },
  },
});

const baseSignOut = authClient.signOut;

export const {
  signIn,
  signUp,
  useSession,
  getSession,
} = authClient;

// Wrap signOut so we always wipe every piece of the previous user's state,
// even if the server call fails (e.g. token already expired server-side):
//   - bearer token in localStorage + module cache
//   - active workspace/project scope (so the next user doesn't inherit it)
//   - React Query cache (so the previous user's workspace/project/credential
//     lists don't briefly flash on the next login before refetches)
//
// We deliberately do NOT touch theme preference or any pre-pivot
// `memkernel-*` keys from earlier app versions — they're not ours.
export async function signOut(...args: Parameters<typeof baseSignOut>) {
  try {
    return await baseSignOut(...args);
  } finally {
    clearAuthToken();
    clearScope();
    queryClient.clear();
  }
}
